import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Modal, 
  Button, 
  CircularProgress
} from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { tokens, withAlpha } from '../../Common/designTokens'; // added

const COMMENT_FIELD_HEIGHT = 160; // px, permanent

const textAreaStyle = {
  width: '100%',
  minHeight: `${COMMENT_FIELD_HEIGHT}px`,
  maxHeight: `${COMMENT_FIELD_HEIGHT}px`,
  height: `${COMMENT_FIELD_HEIGHT}px`,
  padding: '8px',
  boxSizing: 'border-box',
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: '8px',
  backgroundColor: tokens.colors.white,
  resize: 'none',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  overflowX: 'hidden',
  lineHeight: '32px',
  color: tokens.colors.fontColour,
};

const UpdateCharacteristicsStatusModal = ({ 
  open, 
  onClose, 
  onUpdateStatus
}) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(''); // Add error state

  const handleCommentChange = (e) => {
    if (e.target.value.length <= 200) {
      setComment(e.target.value);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(''); // Clear previous errors
    try {
      await onUpdateStatus(comment);
    } catch (err) {
      setError(err.message || 'Failed to update characteristic status');
      setIsSubmitting(false);
      return; // Don't close modal on error
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setComment('');
      setError(''); // Clear error on close
      onClose();
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 4,
    borderRadius: "8px",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  return (
    <Modal 
      open={open} 
      onClose={!isSubmitting ? handleClose : undefined}
      aria-labelledby="update-characteristics-modal-title"
    >
      <Box sx={modalStyle}>
        <Typography 
          id="update-characteristics-modal-title" 
          variant="h6" 
          component="h2"
          sx={{ color: tokens.colors.fontColour, mb: 2, textAlign: 'center', fontWeight: 'bold' }}
        >
          Inventory Data Not Available 
        </Typography>
        
        <Typography sx={{ color: tokens.colors.fontColour85, mb: 2, textAlign: 'center' }}>
          Would you like to update the characteristic status with 'Not Available' data?
        </Typography>
        
        {error && (
          <Box sx={{ 
            mb: 2,
            p: 2,
            backgroundColor: withAlpha(tokens.palette.danger, 0.133),
            border: `1px solid ${tokens.palette.danger}`,
            borderRadius: '8px',
          }}>
            <Typography sx={{ 
              color: tokens.palette.danger,
              fontSize: '14px',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              {error}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ width: '100%', position: 'relative', mb: 2 }}>
          <TextareaAutosize
            name="comments"
            value={comment}
            onChange={handleCommentChange}
            placeholder="Enter remarks"
            minRows={5}
            maxRows={5}
            style={textAreaStyle}
            disabled={isSubmitting}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 8,
              color: comment.length >= 180 ? tokens.palette.primary : tokens.colors.fontColour85,
              fontWeight: comment.length >= 180 ? 'bold' : 'normal',
              fontSize: '0.75rem',
              pointerEvents: 'none'
            }}
          >
            {comment.length}/200
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ 
              textTransform: "none", 
              fontWeight: "700", 
              color: tokens.colors.fontColour,
              height: '40px',
              borderColor: tokens.colors.bodyBorderColor50,
              width: '120px',
              borderRadius: "8px",
              '&:hover': {
                borderColor: tokens.colors.bodyBorderColor50,
                backgroundColor: tokens.colors.defaultGridHoverColor,
              }
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ 
              textTransform: "none", 
              fontWeight: "700", 
              backgroundColor: tokens.palette.primary,
              color: tokens.colors.white,
              '&:hover': { backgroundColor: tokens.palette.primaryA80 },
               width: '120px',
               height: '40px',
               borderRadius: "8px",
            }}
          >
            {isSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Yes'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateCharacteristicsStatusModal;
